import { useDate } from '@/modules/drivers/stores/dateStore'
import { toast } from '@/lib/toasts/api'
import usePrintService from '@/modules/drivers/services/PrintService'
import { ReportType } from '@/app/constants/reportType'
import { TaskType } from '@/app/constants/taskType'
import { useTask } from '@/modules/shared/context/TaskContext'
import { format } from 'date-fns'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'

interface PrintArgs {
  selectedIds: Array<number | string> | undefined
  drivers: Array<{ id: number | string }>
  companyId: string | undefined
  excludedViolations?: number[]
}

export type ViolationReportKind =
  | 'violations'
  | 'violationsWithActivities'
  | 'groupedViolations'
  | 'instruction'

const usePrintViolations = () => {
  const { t } = useLocalize()
  const { range, formatApiDate } = useDate()
  const { getPdf } = usePrintService()
  const { addTask } = useTask()
  const { isGenerating, busyTooltip, runPrintJob } = usePrintJobGuard()

  const submitPrint = async (
    reportType: ReportType,
    { selectedIds, drivers, companyId }: PrintArgs,
    errorKey: string,
    data?: Record<string, unknown>,
  ) => {
    if (!range?.startDate || !range?.endDate) {
      toast.warning({ text: t('common.select-date-range') })
      return false
    }

    const driverIdsToUse =
      selectedIds && selectedIds.length > 0
        ? selectedIds.map(String)
        : drivers.map((d) => String(d.id))

    if (driverIdsToUse.length === 0) {
      toast.warning({ text: t('driverModule.select-drivers') })
      return false
    }

    try {
      const startDate = new Date(
        range.startDate.year!,
        range.startDate.month! - 1,
        range.startDate.day!,
      )
      const endDate = new Date(
        range.endDate.year!,
        range.endDate.month! - 1,
        range.endDate.day!,
      )

      const startTime = formatApiDate(startDate)
      const endTime = encodeURIComponent(
        `${format(endDate, 'YYYY-MM-DD')} 23:59`,
      )

      const taskId = await getPdf({
        companyId,
        reportType,
        drivers: driverIdsToUse,
        startTime,
        endTime,
        data,
      })
      addTask(taskId, TaskType.Printout, reportType)
      return true
    } catch (error) {
      console.error(`Error printing ${reportType}:`, error)
      toast.error({ text: t(errorKey) })
      return false
    }
  }

  const REPORTS: Record<
    ViolationReportKind,
    {
      reportType: ReportType
      errorKey: string
      data?: Record<string, unknown>
      honorsPrintSelection?: boolean
    }
  > = {
    violations: {
      reportType: ReportType.VIOLATIONS,
      errorKey: 'driverModule.report-generation-error',
      data: { printDayActivities: false, printDayNotes: false },
      honorsPrintSelection: true,
    },
    violationsWithActivities: {
      reportType: ReportType.VIOLATIONS,
      errorKey: 'driverModule.report-generation-error',
      data: { printDayActivities: true, printDayNotes: false },
      honorsPrintSelection: true,
    },
    groupedViolations: {
      reportType: ReportType.GROUPED_VIOLATIONS,
      errorKey: 'driverModule.report-generation-error',
      data: { showDetails: true, driverCharge: true },
      honorsPrintSelection: true,
    },
    instruction: {
      reportType: ReportType.DRIVER_INSTRUCTION,
      errorKey: 'driverModule.instruction-generation-error',
    },
  }

 
  const printReports = (kinds: ViolationReportKind[], args: PrintArgs) =>
    runPrintJob(async () => {
      const excludedViolations = args.excludedViolations ?? []
      for (const kind of kinds) {
        const report = REPORTS[kind]
        const data =
          report.honorsPrintSelection && excludedViolations.length > 0
            ? { ...report.data, excludedViolations }
            : report.data
        await submitPrint(report.reportType, args, report.errorKey, data)
      }
    })

  const printSingle = (kind: ViolationReportKind, args: PrintArgs) =>
    printReports([kind], args)

  const handlePrintDriverViolations = (args: PrintArgs) =>
    printSingle('violations', args)

  const handlePrintDriverViolationsWithActivities = (args: PrintArgs) =>
    printSingle('violationsWithActivities', args)

  const handlePrintGroupedViolations = (args: PrintArgs) =>
    printSingle('groupedViolations', args)

  const handlePrintInstruction = (args: PrintArgs) =>
    printSingle('instruction', args)

  return {
    isGenerating,
    busyTooltip,
    printReports,
    handlePrint: handlePrintDriverViolationsWithActivities,
    handlePrintDriverViolations,
    handlePrintDriverViolationsWithActivities,
    handlePrintGroupedViolations,
    handlePrintInstruction,
  }
}

export default usePrintViolations
